import { createPanelFromPreset } from 'features/charts-v2/store'
import { METRICS } from 'features/charts-v2/types'
import {
  SENTIMENT_OVER_TIME_PRESET,
  SENTIMENT_RANKING_PRESET,
  THEME_BRAND_SENTIMENT_PRESET,
} from 'features/charts-v2/types/ChartPresets'
import type {
  Breakdown,
  ChartPanel,
  ChartsV2State,
} from 'features/charts-v2/types/filter-hash/v2'
import { translateV1FiltersToPanel } from 'features/charts-v2/utils/translate-v1-filters-to-panel'
import type { Filters } from 'types/project'

// ---------------------------------------------------------------------------
// Charts-V2 panel builders for the catalog detail page.
//
// These map the page's v1 chart configs (the `6_4_17`, `9_3_19`, `6_5_17`
// chartIds and their lens/tier1) onto charts-v2 `ChartPanel`s, reusing the
// canonical preset → createPanelFromPreset → translateV1FiltersToPanel pipeline
// so the same builder serves both inline rendering and the save-then-redirect
// flow (mirroring `openProductBreakdown` in the v2 summary panel).
// ---------------------------------------------------------------------------

/**
 * Map a catalog lens (the v1 `tier1` field) to its charts-v2 breakdown.
 *
 * `source_type` is a NonGroupableBreakdown (no `grouped` field); the rest are
 * groupable. `grouped` is ignored for source_type.
 */
export function lensToBreakdown(lens: string, grouped: boolean): Breakdown {
  switch (lens) {
    case 'product_hierarchy':
      return { kind: 'standard', id: 'product', grouped }
    case 'theme':
      return { kind: 'standard', id: 'theme', grouped }
    case 'brand':
      return { kind: 'standard', id: 'brand', grouped }
    case 'source_type':
      return { kind: 'standard', id: 'source_type' }
    default:
      return { kind: 'standard', id: 'product', grouped }
  }
}

/**
 * Map the ranking chart's v1 `tier1` ("Theme" | "Product", from
 * `nextStepsConstants`) to its grouped charts-v2 breakdown.
 */
export function rankingTierToBreakdown(tier1: string): Breakdown {
  if (tier1 === 'Theme') {
    return { kind: 'standard', id: 'theme', grouped: true }
  }
  return { kind: 'standard', id: 'product', grouped: false }
}

function applyFilters(base: ChartPanel, filters: Filters): ChartPanel {
  return { ...base, ...translateV1FiltersToPanel(filters, base) }
}

/**
 * #1 — "Sentiment and Volume Over Time" (v1 `6_4_17_sentiment`).
 * Sentiment + sentence volume over time, broken down by the active lens.
 */
export function buildSentimentVolumePanel(
  lens: string,
  filters: Filters,
): ChartPanel {
  const base = applyFilters(
    createPanelFromPreset(SENTIMENT_OVER_TIME_PRESET, 0),
    filters,
  )
  return {
    ...base,
    metrics: [METRICS.SENTIMENT.id, METRICS.SENTENCE_COUNT.id],
    breakdowns: [lensToBreakdown(lens, lens === 'theme')],
    title: 'Sentiment and Volume Over Time',
    isCustomTitle: true,
  }
}

/**
 * #2 — "Theme Sentiment Matrix" (v1 `9_3_19_sentiment`, brand lens only).
 * Theme × brand sentiment heatmap.
 */
export function buildThemeMatrixPanel(filters: Filters): ChartPanel {
  const base = applyFilters(
    createPanelFromPreset(THEME_BRAND_SENTIMENT_PRESET, 0),
    filters,
  )
  return {
    ...base,
    breakdowns: [
      { kind: 'standard', id: 'theme', grouped: true },
      { kind: 'standard', id: 'brand', grouped: false },
    ],
    scaleVolume: true,
    title: 'Theme Sentiment Matrix',
    isCustomTitle: true,
  }
}

/**
 * #3 — "Worst performing X" ranking (v1 `6_5_17_sentiment`).
 * Sentiment ranking by theme/product, ascending so the worst appear first.
 */
export function buildWorstPerformingPanel(
  rankingTier: string,
  filters: Filters,
): ChartPanel {
  const base = applyFilters(
    createPanelFromPreset(SENTIMENT_RANKING_PRESET, 0),
    filters,
  )
  return {
    ...base,
    breakdowns: [rankingTierToBreakdown(rankingTier)],
    sortDirection: 'ASC',
    pageSize: 10,
    title: `Worst Performing ${rankingTier === 'Theme' ? 'Themes' : 'Products'}`,
    isCustomTitle: true,
  }
}

/**
 * Wrap a single panel in the `ChartsV2State` envelope the state service
 * persists and the `/charts/:hash` route hydrates from. Mirrors the state
 * object built in `openProductBreakdown`.
 */
export function panelToV2State(panel: ChartPanel): ChartsV2State {
  return {
    v2: {
      panels: [{ ...panel, order: 0 }],
      globalSettings: {
        syncYAxis: true,
        syncXAxis: true,
        syncDisplaySettings: false,
      },
      activePanelId: panel.id,
    },
  }
}
